import CheckIcon from '@mui/icons-material/Check';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import Typography from '@mui/material/Typography';
import {
  POST_SORT,
  POST_SORT_DEFINITIONS,
  POST_TYPE_DEFINITIONS,
  type TAvailableTag,
  type TPostType,
} from '@onesource/schemas';
import { useGetAvailableTags } from '@onesource/services';
import { useEffect, useMemo, useRef, useState, type FC, type ReactNode } from 'react';
import {
  MdCheck,
  MdClose,
  MdFilterAltOff,
  MdKeyboardArrowDown,
  MdOutlineCalendarToday,
  MdOutlineGridView,
  MdOutlineLocalOffer,
  MdOutlineSwapVert,
  MdPersonOutline,
} from 'react-icons/md';

import { FLUID_TYPE } from '../fluid-type';
import { renderPostTypeMenuItem, sortPostTypeOptions } from '../post-type-select';
import { TagPickerPanel } from '../tag-picker-panel';
import { DateRangePanel, formatDateRangeValue } from './date-range-filter';
import type { TFeedSearchParams } from './feed-search-params';
import { FILTER_ROSE, FILTER_ROSE_BG, FILTER_ROSE_DARK } from './filter-trigger-button';
import { PostedByPanel } from './posted-by-filter';

type SectionId = 'sort' | 'type' | 'date' | 'member' | 'tags';

type Props = {
  open: boolean;
  onClose: () => void;
  /** Feed sub-space id — the tag section loads the container's available tags */
  spaceId: string;
  filters: TFeedSearchParams;
  onChange: (patch: Partial<TFeedSearchParams>) => void;
};

/** Everything the drawer hosts — only the search stays out in the bar. A
    non-default sort counts as active. */
export const countActiveDrawerFilters = (filters: TFeedSearchParams): number =>
  [filters.types?.length, filters.from || filters.to, filters.by, filters.tags?.length, filters.sort].filter(Boolean)
    .length;

/** "N selected" summary for the multi-select sections, or the single label. */
const multiValueLabel = (count: number, single: string | undefined): string | undefined => {
  if (count === 0) {
    return undefined;
  }
  return count === 1 ? (single ?? '1 selected') : `${count} selected`;
};

type SectionCardProps = {
  icon: ReactNode;
  label: string;
  /** Applied value summary — rose when set, grey placeholder otherwise. */
  value?: string;
  /** e.g. "All types" / "Any time" — shown when no value is applied. */
  placeholder: string;
  expanded: boolean;
  onToggle: () => void;
  children: ReactNode;
};

/**
 * One filter as a rounded card: icon tile + label/value header that expands
 * to reveal the picker panel. Content unmounts when collapsed so panel-local
 * state (pending date range, member search) reseeds on every expand.
 */
const SectionCard: FC<SectionCardProps> = ({ icon, label, value, placeholder, expanded, onToggle, children }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const active = !!value;

  return (
    <Box
      ref={cardRef}
      sx={{
        border: '1px solid',
        // Active filters are highlighted as whole cards, so they read at a
        // glance when the drawer opens on the collapsed overview.
        borderColor: active ? `${FILTER_ROSE}66` : expanded ? 'grey.300' : '#ececec',
        borderRadius: '14px',
        bgcolor: 'common.white',
        overflow: 'hidden',
        transition: 'border-color 0.15s ease',
        // The section list is a scrolling flex column — without this, flex
        // shrinks the cards (the expanded panel most, since its scrollable
        // content has a tiny min-height) to fit the sheet instead of letting
        // the list scroll, which cut off the bottom sections.
        flexShrink: 0,
      }}
    >
      <ButtonBase
        onClick={onToggle}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          px: 1.5,
          py: '10px',
          textAlign: 'left',
          bgcolor: active ? FILTER_ROSE_BG : 'transparent',
          transition: 'background-color 0.15s ease',
        }}
      >
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: '10px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            bgcolor: active ? 'common.white' : '#f5f5f5',
            color: active ? FILTER_ROSE : '#777',
            transition: 'background-color 0.15s ease, color 0.15s ease',
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            noWrap
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontSize: FLUID_TYPE.labelSm,
              lineHeight: 1.4,
              fontWeight: 700,
              color: '#333',
            }}
          >
            {label}
          </Typography>
          <Typography
            noWrap
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontSize: FLUID_TYPE.caption,
              lineHeight: 1.4,
              fontWeight: value ? 700 : 400,
              color: value ? FILTER_ROSE_DARK : '#9a9a9a',
            }}
          >
            {value ?? placeholder}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
            color: active ? `${FILTER_ROSE}99` : '#9a9a9a',
            flexShrink: 0,
            transition: 'transform 0.2s ease',
            transform: expanded ? 'rotate(180deg)' : 'none',
          }}
        >
          <MdKeyboardArrowDown size={20} />
        </Box>
      </ButtonBase>
      <Collapse
        in={expanded}
        unmountOnExit
        // Expanded panels can extend past the sheet's fold — align the card's
        // END so its bottom actions (e.g. the calendar's Clear/Apply) are on
        // screen once the expand settles.
        onEntered={() => cardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })}
      >
        {/* Generous cap — a panel taller than this scrolls inside the card so
            its bottom actions stay reachable on short phones. */}
        <Box
          sx={{ borderTop: '1px solid #f0f0f0', maxHeight: '62dvh', overflowY: 'auto', overscrollBehavior: 'contain' }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

/**
 * Mobile replacement for the row of filter popovers — a bottom sheet with one
 * expandable card per filter (post type, date range, posted by, tags), reusing
 * the same panels as the desktop popovers. Search and sort stay outside in
 * the filter bar.
 */
export const FeedFilterDrawer: FC<Props> = ({ open, onClose, spaceId, filters, onChange }) => {
  const [expanded, setExpanded] = useState<SectionId | null>(null);

  // The drawer stays mounted between opens — collapse whatever section was
  // left expanded so each open starts from the overview, where the active
  // filters are visible at a glance via their highlighted cards.
  useEffect(() => {
    if (!open) {
      setExpanded(null);
    }
  }, [open]);

  const selectedTypes = filters.types ?? [];
  const selectedTagValues = filters.tags ?? [];

  const sortedTypeOptions = useMemo(() => sortPostTypeOptions(POST_TYPE_DEFINITIONS), []);

  // Lazy-load tags: only once the section expands, or when a selection came in
  // from the URL and the header needs its label. `usedOnly` hides tags no post
  // carries yet — filtering by them would only ever yield an empty feed.
  const { data: availableTags, isLoading: isLoadingTags } = useGetAvailableTags(spaceId, {
    enabled: open && (expanded === 'tags' || selectedTagValues.length > 0),
    usedOnly: true,
  });
  const tagOptions = useMemo(() => availableTags ?? [], [availableTags]);
  const selectedTagOptions = useMemo(
    () => tagOptions.filter(o => filters.tags?.includes(o.value)),
    [tagOptions, filters.tags],
  );

  const activeCount = countActiveDrawerFilters(filters);

  const toggleSection = (section: SectionId) => {
    setExpanded(prev => (prev === section ? null : section));
  };

  const handleTypeToggle = (type: TPostType) => {
    const next = selectedTypes.includes(type) ? selectedTypes.filter(t => t !== type) : [...selectedTypes, type];
    onChange({ types: next.length > 0 ? next : undefined });
  };

  const handleTagsChange = (tags: TAvailableTag[]) => {
    onChange({ tags: tags.length > 0 ? tags.map(t => t.value) : undefined });
  };

  const currentSort = filters.sort ?? POST_SORT.RECENT_ACTIVITY;

  const handleSortSelect = (sort: (typeof POST_SORT_DEFINITIONS)[number]['value']) => {
    // The default (recent activity) is represented by omitting the param.
    onChange({ sort: sort === POST_SORT.RECENT_ACTIVITY ? undefined : sort });
    setExpanded(null);
  };

  const handleClearAll = () => {
    onChange({
      types: undefined,
      from: undefined,
      to: undefined,
      by: undefined,
      byName: undefined,
      tags: undefined,
      sort: undefined,
    });
  };

  return (
    <SwipeableDrawer
      anchor="bottom"
      open={open}
      onClose={onClose}
      onOpen={() => undefined}
      disableSwipeToOpen
      PaperProps={{
        // The paper must be a flex column for the section list to scroll while
        // the header and footer stay pinned — otherwise an expanded section
        // pushes the footer past maxHeight and its bottom actions get cut off.
        sx: { borderRadius: '20px 20px 0 0', maxHeight: '92dvh', display: 'flex', flexDirection: 'column' },
      }}
    >
      {/* Grab handle */}
      <Box sx={{ display: 'flex', justifyContent: 'center', pt: '10px' }}>
        <Box sx={{ width: 40, height: 4, borderRadius: 2, bgcolor: 'grey.300' }} />
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, pt: 1.25, pb: 1.5 }}>
        <Box>
          <Typography
            sx={{
              fontFamily: 'Figtree, sans-serif',
              fontSize: FLUID_TYPE.title,
              lineHeight: 1.3,
              fontWeight: 800,
              color: '#1a1a1a',
            }}
          >
            Filters
          </Typography>
          <Typography
            sx={{ fontFamily: 'Figtree, sans-serif', fontSize: FLUID_TYPE.caption, lineHeight: 1.5, color: '#808080' }}
          >
            {activeCount > 0 ? `${activeCount} active` : 'Narrow down the feed'}
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          aria-label="Close filters"
          sx={{ bgcolor: '#f4f4f4', color: '#555', '&:hover': { bgcolor: '#ececec' } }}
        >
          <MdClose size={18} />
        </IconButton>
      </Box>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overscrollBehavior: 'contain',
          px: 2,
          pb: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        <SectionCard
          icon={<MdOutlineSwapVert size={18} />}
          label="Sort by"
          placeholder="Most recent activity"
          value={filters.sort ? POST_SORT_DEFINITIONS.find(d => d.value === filters.sort)?.label : undefined}
          expanded={expanded === 'sort'}
          onToggle={() => toggleSection('sort')}
        >
          <Box sx={{ py: 0.75 }}>
            {POST_SORT_DEFINITIONS.map(({ value: sortValue, label }) => {
              const selected = currentSort === sortValue;
              return (
                <MenuItem
                  key={sortValue}
                  selected={selected}
                  onClick={() => handleSortSelect(sortValue)}
                  sx={{
                    borderRadius: '10px',
                    mx: 0.75,
                    my: 0.25,
                    px: 1.25,
                    py: 0.75,
                    gap: 1,
                    fontFamily: 'Figtree, sans-serif',
                    fontSize: FLUID_TYPE.label,
                    fontWeight: selected ? 600 : 400,
                    color: '#333',
                    '&.Mui-selected': {
                      bgcolor: theme => `${theme.palette.primary.main}14`,
                      '&:hover': { bgcolor: theme => `${theme.palette.primary.main}1F` },
                    },
                  }}
                >
                  <Box component="span" sx={{ flex: 1 }}>
                    {label}
                  </Box>
                  {selected && <CheckIcon fontSize="small" sx={{ color: 'primary.main', flexShrink: 0 }} />}
                </MenuItem>
              );
            })}
          </Box>
        </SectionCard>
        <SectionCard
          icon={<MdOutlineGridView size={18} />}
          label="Post type"
          placeholder="All types"
          value={multiValueLabel(
            selectedTypes.length,
            POST_TYPE_DEFINITIONS.find(d => d.value === selectedTypes[0])?.label,
          )}
          expanded={expanded === 'type'}
          onToggle={() => toggleSection('type')}
        >
          <Box sx={{ py: 0.75 }}>
            {sortedTypeOptions.map(def =>
              renderPostTypeMenuItem(def, selectedTypes.includes(def.value), () => handleTypeToggle(def.value)),
            )}
          </Box>
        </SectionCard>
        <SectionCard
          icon={<MdOutlineLocalOffer size={17} />}
          label="Tags"
          placeholder="Any tags"
          value={multiValueLabel(selectedTagValues.length, selectedTagOptions[0]?.label ?? 'Tag')}
          expanded={expanded === 'tags'}
          onToggle={() => toggleSection('tags')}
        >
          <TagPickerPanel
            title="Filter by tags"
            options={tagOptions}
            value={selectedTagOptions}
            onChange={handleTagsChange}
            loading={isLoadingTags}
            autoFocusSearch={false}
            hideTitle
          />
        </SectionCard>
        <SectionCard
          icon={<MdPersonOutline size={18} />}
          label="Posted by"
          placeholder="Anyone"
          value={filters.by ? filters.byName || 'Member' : undefined}
          expanded={expanded === 'member'}
          onToggle={() => toggleSection('member')}
        >
          <PostedByPanel
            spaceId={spaceId}
            value={filters.by}
            onSelect={user => {
              onChange({ by: user?.id, byName: user?.displayName });
              setExpanded(null);
            }}
            autoFocusSearch={false}
            hideTitle
          />
        </SectionCard>
        <SectionCard
          icon={<MdOutlineCalendarToday size={17} />}
          label="Date range"
          placeholder="Any time"
          value={formatDateRangeValue(filters.from, filters.to)}
          expanded={expanded === 'date'}
          onToggle={() => toggleSection('date')}
        >
          <DateRangePanel
            from={filters.from}
            to={filters.to}
            onChange={range => onChange(range)}
            onDone={() => setExpanded(null)}
            hideTitle
            compact
          />
        </SectionCard>
      </Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 2,
          pt: 1.5,
          // Keep the actions above the home-indicator / browser bottom UI.
          pb: 'calc(12px + env(safe-area-inset-bottom))',
          borderTop: '1px solid #f0f0f0',
          flexShrink: 0,
        }}
      >
        <Button
          variant="text"
          onClick={handleClearAll}
          disabled={activeCount === 0}
          startIcon={<MdFilterAltOff size={16} />}
          sx={{
            flex: 1,
            borderRadius: '20px',
            textTransform: 'none',
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            color: 'rgba(0, 0, 0, 0.7)',
            '&:hover': { bgcolor: '#f4f4f4', color: 'rgba(0, 0, 0, 0.85)' },
          }}
        >
          Clear all
        </Button>
        <Button
          variant="contained"
          onClick={onClose}
          startIcon={<MdCheck size={16} />}
          sx={{
            flex: 1,
            borderRadius: '20px',
            textTransform: 'none',
            fontFamily: 'Figtree, sans-serif',
            fontWeight: 600,
          }}
        >
          Done
        </Button>
      </Box>
    </SwipeableDrawer>
  );
};
